import React, { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, Row, Col, ModalFooter, Form, FormGroup, Label } from 'reactstrap';
import OutlineClear from 'react-md-icon/dist/OutlineClear';
import HelloSign from 'hellosign-embedded';
import { get } from 'lodash';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import FileViewer from '../../components/FileViewer/index';
import { downloadFromUrl, setFileUrl } from '../../helpers';
import {
  displayToaster, hideToaster, toggleDrawer,
  GET_EDIT_TEMPLATE_URL, clearLoadingState,
  getCompanyDocCategories, GET_COMPANY_DOCUMENT_CATEGORIES,
  pushRecentDocCategory, getEditTemplateUrl,
  GET_DOCUMENT_PREVIEW_URL, GET_DOCUMENT_DOWNLOAD_URL,
  updateCompanyDocument, UPDATE_COMPANY_DOCUMENT, LOADING_PROP_TYPE,
} from '../../modules/index';
import { UploadDropzone, Drawer, DrawerHeader, LockedModalBody, UnsupportedError, ErrorMessage } from '../../components/index';
import SubmitButton from '../../components/Forms/Components/SubmitButton/SubmitButton';
import FieldBuilder from '../../components/Forms/FieldBuilder/FieldBuilder';
import { APPLICATION_ROOT, ESIGN_DEV_MODE, MAX_DOC_CATEGORY_LENGTH, IMAGE_TYPES } from '../../constants/index';

const DEFAULT_STATE = {
  file: undefined,
  helloSignClient: undefined,
  category: undefined,
  fileCleared: false,
  isHelloSignTemplate: undefined,
  isOnboardingDocument: undefined,
  isPublishedToEmployee: undefined,
};

class EditTemplate extends Component {
  constructor(props) {
    super(props);
    this.state = DEFAULT_STATE;

    this.handleDrawer = this.handleDrawer.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onClear = this.onClear.bind(this);
  }

  componentDidMount() {
    this.props.getDocCategoryList();
  }

  componentDidUpdate(prevProps) {
    const { editTemplateData: { url, clientId }, loading } = this.props;
    const { editTemplateData: { url: prevUrl }, loading: prevLoading } = prevProps;
    const container = document.getElementById('template-container');
    let client;
    if (url && url !== prevUrl) {
      client = new HelloSign({ clientId });
      client.open(
        url,
        { container, skipDomainVerification: ESIGN_DEV_MODE }
      );
    }
    if (client) {
      client.on('createTemplate', () => {
        this.props.displayToaster([
          'success',
          <FormattedMessage id="TEMPLATE_UPDATED" />,
          'app',
          5000,
          true,
        ]);
        this.handleDrawer();
        client.close();
      });
      client.on('ready', () => {
        this.setState({ helloSignClient: client });
      });
    }
    if (prevLoading[UPDATE_COMPANY_DOCUMENT] === 'loading' && loading[UPDATE_COMPANY_DOCUMENT] === 'complete') {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_UPDATED" />,
        'app',
        5000,
        true,
      ]);
      this.handleDrawer();
    }
  }

  onDrop(file) {
    this.setState({ file });
  }

  onClear() {
    this.setState({ fileCleared: true });
  }

  onSubmit = (values) => {
    const { selectedDocument } = this.props;
    const { file } = this.state;
    const { companyId } = get(window, 'asure.appSettings.selectedCompany');
    const {
      category,
      title,
      isPublishedToEmployee,
      isOnboardingDocument,
    } = values;
    this.props.updateCompanyDocument(
      file,
      companyId,
      title,
      category,
      isPublishedToEmployee,
      selectedDocument.id,
      isOnboardingDocument
    );
  }

  handleDrawer() {
    const { drawerIsOpen } = this.props;
    const { helloSignClient } = this.state;

    if (helloSignClient && drawerIsOpen) helloSignClient.close();
    this.props.toggleDrawer(!drawerIsOpen);
    this.props.hideToaster('drawer');
    this.props.clearLoadingState(UPDATE_COMPANY_DOCUMENT);
    // Allowing the drawer to close before resetting state makes for a cleaner animation
    setTimeout(() => this.setState(DEFAULT_STATE), 500);
  }

  loadMoreCategories() {
    const { docCategories: { next } } = this.props;
    if (next && this.props.loading[GET_COMPANY_DOCUMENT_CATEGORIES] === 'complete') {
      this.props.getDocCategoryList(next);
    }
  }

  groupCategories = (docCategories) => {
    const { recent, categories } = docCategories;
    return [
      { label: 'RECENT', options: recent },
      { label: 'CATEGORIES', options: categories },
    ];
  }

  toggleVisibility() {
    const { selectedDocument } = this.props;
    if (this.state.isPublishedToEmployee) {
      this.setState({ isPublishedToEmployee: false });
    } else if (this.state.isPublishedToEmployee === false) {
      this.setState({ isPublishedToEmployee: true });
      // if isPublishedToEmployee is undefined, we need to check the document status.
    } else {
      this.setState({ isPublishedToEmployee: !selectedDocument.isPublishedToEmployee });
    }
  }

  render() {
    const {
      loading,
      drawerIsOpen,
      selectedDocument,
      docCategories,
      filePreview: { url, mimeType },
    } = this.props;
    const {
      file,
      fileCleared,
      isPublishedToEmployee,
      isOnboardingDocument,
    } = this.state;
    const { companyId } = get(window, 'asure.appSettings.selectedCompany');
    const isFormLocked = selectedDocument.isEsignatureDocument;
    const signatureOptions = selectedDocument.isEsignatureDocument;
    const isSubmitting = loading[UPDATE_COMPANY_DOCUMENT] === 'loading';

    const containerClasses = ['w-100', 'h-100'];
    const buttonClasses = ['d-flex', 'row-action', 'upload-cancel', 'position-absolute'];
    const iconStyles = { fontSize: '1.5rem', color: 'var(--color-neutral-500)' };
    const isLoading =
      loading[GET_EDIT_TEMPLATE_URL] === 'loading' ||
      loading[GET_DOCUMENT_PREVIEW_URL] === 'loading' ||
      loading[GET_DOCUMENT_DOWNLOAD_URL] === 'loading';
    if (isLoading) {
      containerClasses.push('hello-sign-container');
      containerClasses.push('loading');
      containerClasses.push('p-0');
    }

    const fileUrl = setFileUrl(url);

    let documentPreview;
    const previewRowClasses = new Set(['align-items-center', 'p-3', 'h-75']);
    const previewColClasses = new Set(['h-100', 'p-0']);
    if (!isLoading) {
      previewColClasses.add('border-dashed');
      if (!IMAGE_TYPES.includes(mimeType)) previewRowClasses.delete('h-75');
      if (!fileCleared) {
        documentPreview = (
          <Fragment>
            <Button
              color="white"
              size="sm"
              className={`p-0 ${buttonClasses.join(' ')}`}
              disabled={isLoading || isFormLocked || selectedDocument.isLegacyDocument}
              onClick={this.onClear}
            >
              <OutlineClear style={iconStyles} />
            </Button>
            <FileViewer
              fileType={(mimeType && mimeType.replace('.', '')) || ''}
              filePath={fileUrl || ''}
              unsupportedComponent={
                () => (
                  <UnsupportedError
                    messageId="FILE_COULD_NOT_BE_PREVIEWED"
                    ctaMessageId="DOWNLOAD"
                    onClick={() => downloadFromUrl(fileUrl, selectedDocument.filename)}
                    classes="p-3"
                  />
                )
              }
              loadingComponent={() => (
                <div className="file-viewer-loading d-flex h-100 w-100" />
              )}
            />
          </Fragment>
        );
      } else {
        previewRowClasses.delete('h-75');
        previewColClasses.delete('border-dashed');
        documentPreview = (
          <FormGroup>
            <UploadDropzone onDrop={this.onDrop} onError={this.props.displayToaster} />
          </FormGroup>
        );
      }
    }
    const formFieldData = [
      {
        key: 'category',
        type: 'creatable',
        data: {
          colClasses: ['col col-md-8'],
          name: 'category',
          label: 'CATEGORY',
          defaultOptionValue: selectedDocument.category || '',
          defaultOptionLabel: selectedDocument.category || '',
          options: this.groupCategories(docCategories),
          placeholder: 'Select a category',
          maxLength: MAX_DOC_CATEGORY_LENGTH,
          customStyles: {
            singleValue: provided => ({ ...provided, whiteSpace: 'pre !important' }),
            menu: provided => ({
              ...provided,
              whiteSpace: 'pre !important',
              maxHeight: '20vh',
              overflow: 'scroll',
            }),
            menuList: provided => ({ ...provided, maxHeight: '20vh' }),
          },
          onScrollToBottom: this.loadMoreCategories(),
          isComponentLoading: loading[GET_COMPANY_DOCUMENT_CATEGORIES] === 'loading',
        },
      },
    ];
    const submitDisabled = loading[GET_EDIT_TEMPLATE_URL] === 'error' || loading[GET_DOCUMENT_PREVIEW_URL] === 'error';
    const formData = {
      initialValues: {
        title: selectedDocument.title || selectedDocument.filename,
      },
      validationSchema: Yup.object().shape({
        category: Yup.string().max(MAX_DOC_CATEGORY_LENGTH, 'CATEGORY_CANNOT_EXCEED_50_CHARACTERS'),
        title: Yup.string().required('PLEASE_SPECIFY_A_DISPLAY_NAME'),
      }),
    };
    return (
      <Fragment>
        <Drawer
          modalClassName={!selectedDocument.isHelloSignTemplate ? 'drawer wide' : 'drawer wider'}
          toggle={this.handleDrawer}
          isOpen={drawerIsOpen && window.location.hash.indexOf('create-template') < 0}
          parentselector={APPLICATION_ROOT}
          backdrop={loading[GET_EDIT_TEMPLATE_URL] === 'loading' ? 'static' : true}
        >
          <DrawerHeader
            title="DOCUMENT_EDITOR"
            toggle={this.handleDrawer.bind(this)}
          />
          {
          selectedDocument.isHelloSignTemplate ? (
            <LockedModalBody className="p-4 h-100">
              <div id="template-container" className={containerClasses.join(' ')} />
            </LockedModalBody>
          ) : (
            <Formik
              initialValues={formData.initialValues}
              validateOnChange
              validateOnBlur
              validationSchema={formData.validationSchema}
              validate={values => !values.title || (fileCleared && !file)}
            >
              {({
                  values,
                  errors,
                  touched,
                }) => (
                  <Fragment>
                    <LockedModalBody className="p-4 d-flex flex-column">
                      <Form>
                        <Row>
                          <Col className="col col-md-8">
                            <FormGroup>
                              <Label className="required" for="title"><FormattedMessage id="TITLE" /></Label>
                              <Field
                                name="title"
                                type="text"
                                className={errors.title && touched.title ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Title"
                                disabled={isSubmitting}
                              />
                              <ErrorMessage errorText={errors.title} />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FieldBuilder
                          formFieldData={formFieldData}
                          errors={errors}
                          touched={touched}
                          isSubmitting={isSubmitting}
                        />
                        <Row className="pb-3">
                          <Col>
                            <Row className="pl-3 mb-2 reduced-text"><FormattedMessage id="ONBOARDING_DOCUMENT" /></Row>
                            <Row className="pl-3 reduced-text">
                              <label className="reduced-text" htmlFor="no">
                                <input
                                  className="ml-1 mr-2 align-middle"
                                  type="radio"
                                  id="no"
                                  name="onboarding"
                                  value="no"
                                  defaultChecked={!selectedDocument.isOnboardingDocument}
                                  onChange={() => this.setState({ isOnboardingDocument: false })}
                                />
                                <FormattedMessage id="NO" className="align-middle" />
                              </label>
                              <label className="pl-3 reduced-text" htmlFor="yes">
                                <input
                                  className="ml-2 mr-2 align-middle"
                                  type="radio"
                                  id="yes"
                                  name="onboarding"
                                  value="yes"
                                  defaultChecked={selectedDocument.isOnboardingDocument}
                                  onChange={() => this.setState({ isOnboardingDocument: true })}

                                />
                                <FormattedMessage id="YES" className="align-middle" />
                              </label>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Col className="reduced-text pb-2">
                            <FormattedMessage id="SIGNATURE_OPTIONS" />
                            <div className="mt-2 text-muted">
                              { signatureOptions ? <FormattedMessage id="SIMPLE_ESIGNATURE_EDIT" />
                              : <FormattedMessage id="NO_SIGNATURE_REQUIRED" /> }
                            </div>
                          </Col>
                        </Row>
                        <span className={!selectedDocument.isHelloSignTemplate
                           && selectedDocument.isEsignatureDocument ?
                           'd-none' : 'd-block'}
                        >
                          <Row className="mt-1 pt-3 pb-2 pl-3 reduced-text" ><FormattedMessage id="VISIBILITY" /></Row>
                          <Row className="pl-3 text-muted reduced-text">
                            <label htmlFor="visibility">
                              <input
                                className="mr-2 ml-1 align-middle"
                                type="checkbox"
                                id="visibility"
                                name="visibility"
                                value="visibility"
                                defaultChecked={selectedDocument.isPublishedToEmployee}
                                onChange={() => this.toggleVisibility()}
                              />
                              <FormattedMessage id="VISIBILITY_MESSAGE" className="align-middle" />
                            </label>
                          </Row>
                        </span>
                        <Row className="mt-3" />
                        <Row key="file-viewer" className={[...previewRowClasses].join(' ')}>
                          <Col className={[...previewColClasses].join(' ')}>
                            <div className={containerClasses.join(' ')}>
                              {documentPreview}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </LockedModalBody>
                    <ModalFooter>
                      <SubmitButton
                        onSubmit={() => {
                          const { category, title } = values;
                          this.props.updateCompanyDocument(
                            file,
                            companyId,
                            title,
                            category,
                            isPublishedToEmployee,
                            selectedDocument.id,
                            isOnboardingDocument,
                        );
                        }}
                        defaultLabel="SAVE"
                        submittingLabel="SAVE"
                        disabled={
                          submitDisabled ||
                          isFormLocked ||
                          isSubmitting
                        }
                        submitting={isSubmitting}
                      />
                    </ModalFooter>
                  </Fragment>
                  )
                }
            </Formik>
              )
          }
        </Drawer>
      </Fragment>
    );
  }
}

EditTemplate.defaultProps = {
  drawerIsOpen: false,
  selectedDocument: {},
};

EditTemplate.propTypes = {
  editTemplateData: PropTypes.shape({
    clientId: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  displayToaster: PropTypes.func.isRequired,
  loading: LOADING_PROP_TYPE.isRequired,
  drawerIsOpen: PropTypes.bool,
  toggleDrawer: PropTypes.func.isRequired,
  hideToaster: PropTypes.func.isRequired,
  clearLoadingState: PropTypes.func.isRequired,
  getDocCategoryList: PropTypes.func.isRequired,
  filePreview: PropTypes.shape({
    url: PropTypes.string,
    mimeType: PropTypes.string,
  }).isRequired,
  selectedDocument: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    filename: PropTypes.string,
    category: PropTypes.string,
    uploadDate: PropTypes.string,
    isEsignatureDocment: PropTypes.bool,
    extension: PropTypes.string,
    isPublishedToEmployee: PropTypes.bool,
    isLegacyDocument: PropTypes.bool,
    isHelloSignTemplate: PropTypes.bool,
    isOnboardingDocument: PropTypes.bool,
  }),
  docCategories: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    recent: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    next: PropTypes.string,
  }).isRequired,
  updateCompanyDocument: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const {
      resources: {
        templates: {
          editTemplate,
        },
        docs: {
          filePreview,
          docCategories,
          selectedDocument,
          companies,
        },
      },
      appState,
    } = state;
    return {
      companies,
      editTemplateData: editTemplate,
      loading: appState.loading,
      drawerIsOpen: appState.drawer.isOpen,
      selectedDocument,
      filePreview,
      docCategories,
    };
  },
  {
    displayToaster: args => displayToaster(...args),
    toggleDrawer: open => toggleDrawer(open),
    hideToaster: location => hideToaster(location),
    clearLoadingState: action => clearLoadingState(action),
    getDocCategoryList: next => getCompanyDocCategories(next),
    pushRecentDocCategory: category => pushRecentDocCategory(category),
    getEditTemplateUrl: templateId => getEditTemplateUrl(templateId),
    updateCompanyDocument,
  }
)(hot(module)(EditTemplate));



// WEBPACK FOOTER //
// ./src/containers/EditTemplate/EditTemplate.jsx